import React, {
  useMemo,
  useState,
  useCallback,
  useContext,
  useEffect,
} from 'react';

import { InfoCircleFill as InfoCircleFillIcon } from '@styled-icons/bootstrap/InfoCircleFill';
import { api } from 'services/api';
import { toast } from 'shared/toast';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Button,
  ModalFooter,
  FormControl,
  Input,
  Text,
  FormLabel,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  HStack,
  Checkbox,
  Tooltip,
  Icon,
  Flex,
} from '@chakra-ui/react';

import { ModalRootProps } from 'components/Modal/Root';

import AuthContext from 'contexts/AuthContext';

import useAlert from 'hooks/useAlert';
import useModal from 'hooks/useModal';

import { Budget } from 'types/budget';

import BasesTab from './Tabs/Bases';
import FieldsTab from './Tabs/Fields';
import { Header, FormErrors, BaseConfig, Capability } from './types';

type ValidatorResult = {
  isValid: boolean;
  errors: FormErrors;
  basesWithoutMapping?: string[];
};

interface ModalBudgetImportFieldsProps extends ModalRootProps {
  data: {
    budgetId: number;
    budgetDescription: string;
    stageDescription?: string;
    importId: number;
    mappedHeaders: Header;
    headerRow: number;
    baseId: number;
    fileName: string;
  };
}

const modalMargin = 40;

const FIELDS_TAB = 0;
const BASES_TAB = 1;

const ModalBudgetImportFields: React.FC<ModalBudgetImportFieldsProps> = ({
  onConfirm,
  handleClose,
  data: {
    budgetId,
    budgetDescription,
    stageDescription,
    importId,
    mappedHeaders,
    headerRow,
    fileName,
  },
  ...restProps
}) => {
  const { user } = useContext(AuthContext);

  const { openModal } = useModal();
  const { openAlert } = useAlert();

  const [budgetBases, setBudgetBases] = useState<Budget['bases']>([]);

  const [activeTab, setActiveTab] = useState(0);
  const handleTabsChange = (index: number): void => {
    setActiveTab(index);
  };

  const [headers, setHeaders] = useState<Header>(mappedHeaders);
  const actualHeaders = useMemo(() => Object.keys(headers), [headers]);

  const [mappedColumns, setMappedColumns] = useState<Header>(mappedHeaders);

  const [capabilities, setCapabilities] = useState<Capability[]>([]);
  const [isSavingMapping, setIsSavingMapping] = useState(false);

  const budgetImportConfigCapability = useMemo(() => {
    return capabilities.find((cap) => cap.key === 'budget_import_config')
      ?.capability_value;
  }, [capabilities]);

  const [isUpdatingHeader, setIsUpdatingHeader] = useState(false);
  const [newHeaderLine, setNewHeaderLine] = useState(0);

  const [errors, setErrors] = useState<FormErrors>({} as FormErrors);
  const [loading, setLoading] = useState(false);

  const [fields, setFields] = useState({
    item: '',
    type: '',
    code: '',
    base: '',
    description: '',
    unit_measure: '',
    coefficient: '',
  });

  const columnsPayload = useMemo(() => {
    return {
      item: fields.item,
      type: fields.type,
      code: fields.code,
      base: fields.base,
      description: fields.description,
      unit_measure: fields.unit_measure,
      coefficient: fields.coefficient,
    };
  }, [fields]);

  const [baseConfig, setBaseConfig] = useState<BaseConfig>({});
  const [configErrors, setConfigErrors] = useState<FormErrors>(
    {} as FormErrors,
  );

  const basesPayload = useMemo(() => {
    return {
      ...mappedColumns,
      budget_base: Object.fromEntries(
        Object.entries(baseConfig).filter(([, value]) => value.length > 0),
      ),
    };
  }, [baseConfig, mappedColumns]);

  const getBudget = useCallback(async () => {
    try {
      const { data: budgetResponse } = await api.get(`/budget/${budgetId}`, {
        params: {
          organization_id: user.managed_organization.organization.id,
        },
      });

      const budgetData = budgetResponse.data;
      setBudgetBases(budgetData.bases);
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro ao tentar carregar o orçamento',
        status: 'error',
      });

      setBudgetBases([]);
    }
  }, [user, budgetId]);

  useEffect(() => {
    getBudget();
  }, [getBudget]);

  const getCapabilities = useCallback(async () => {
    setLoading(true);

    try {
      const { data: response } = await api.get('/capability', {
        params: {
          'filter[read_only]': 0,
          'filter[object_type]': 'organization',
          'filter[object_id]': user.managed_organization.organization.id,
        },
      });
      const capabilitiesLoaded = response.data;

      setCapabilities(capabilitiesLoaded);
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro ao carregar as configurações!',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    getCapabilities();
  }, [getCapabilities]);

  const handleSaveMappingCapability = useCallback(async () => {
    setLoading(true);

    try {
      if (budgetImportConfigCapability) {
        await api.put(`/capability`, {
          object_type: 'organization',
          object_id: user.managed_organization.organization.id,
          data: [
            {
              id: budgetImportConfigCapability.id,
              value: columnsPayload,
            },
          ],
        });
      } else {
        await api.post('/capability', {
          object_type: 'organization',
          object_id: user.managed_organization.organization.id,
          data: [
            {
              key: 'budget_import_config',
              value: columnsPayload,
            },
          ],
        });
      }
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro ao salvar as configurações de mapeamento',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [columnsPayload, user, budgetImportConfigCapability]);

  const handleSetSavedMapping = useCallback(() => {
    if (budgetImportConfigCapability) {
      const filteredData = Object.fromEntries(
        Object.entries(budgetImportConfigCapability.value).filter(([, value]) =>
          actualHeaders.includes(value),
        ),
      ) as typeof fields;

      setFields(filteredData);
    } else {
      toast({
        description: 'Nenhuma configuração de mapeamento salva encontrada.',
        status: 'info',
      });
    }
  }, [budgetImportConfigCapability, actualHeaders]);

  const handleUpdateHeader = async (): Promise<void> => {
    try {
      setLoading(true);

      const {
        data: {
          data: { mapped_header },
        },
      } = await api.put(`/import/${importId}`, {
        header_row: newHeaderLine,
      });

      setHeaders(mapped_header);
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro inesperado ao atualizar a linha do cabeçalho',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const validateFieldsTab = useCallback((): ValidatorResult => {
    const formErrors: FormErrors = {} as FormErrors;

    Object.keys(fields).forEach((field) => {
      if (!fields[field as keyof typeof fields]) {
        formErrors[field] = 'Campo obrigatório';
      }
    });

    return {
      isValid: Object.keys(formErrors).length === 0,
      errors: formErrors,
    };
  }, [fields]);

  const validateBasesTab = useCallback((): ValidatorResult => {
    const formErrors: FormErrors = {} as FormErrors;

    const hasAtLeastOneConfigured = budgetBases.some(
      (base) => baseConfig[base.id] && baseConfig[base.id].length > 0,
    );

    if (!hasAtLeastOneConfigured) {
      formErrors.bases = 'É necessário configurar pelo menos uma base';
    }

    return {
      isValid: Object.keys(formErrors).length === 0,
      errors: formErrors,
      basesWithoutMapping: budgetBases
        .filter(
          (base) => !baseConfig[base.id] || baseConfig[base.id].length === 0,
        )
        .map((base) => base.id)
        .map(String),
    };
  }, [budgetBases, baseConfig]);

  const submitFields = useCallback(async () => {
    try {
      setLoading(true);

      const { data: response } = await api.put(`/import/${importId}`, {
        mapped_columns: columnsPayload,
        header_row: newHeaderLine || headerRow,
      });

      setMappedColumns(response.data.mapped_columns);
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro inesperado ao importar o arquivo.',
        status: 'error',
      });

      throw err;
    } finally {
      setLoading(false);
    }
  }, [importId, headerRow, columnsPayload, newHeaderLine]);

  const submitBases = useCallback(async () => {
    try {
      setLoading(true);

      await api.put(`/import/${importId}`, {
        mapped_columns: basesPayload,
        header_row: newHeaderLine || headerRow,
      });
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro inesperado ao importar as bases.',
        status: 'error',
      });

      throw err;
    } finally {
      setLoading(false);
    }
  }, [importId, basesPayload, headerRow, newHeaderLine]);

  const closeModal = useCallback((): void => {
    if (onConfirm) onConfirm(importId);
    handleClose();
  }, [onConfirm, importId, handleClose]);

  const warnAboutEmptyBasesConfiguration = useCallback(() => {
    openAlert({
      type: 'warning',
      title: 'Bases sem configuração',
      size: 'lg',
      description: () => (
        <Flex flexDirection="column" width="100%" gap={2}>
          <Text>
            Existem bases do orçamento sem configuração de correspondência com
            as bases encontradas no arquivo importado.
          </Text>

          <Text>Deseja continuar?</Text>
        </Flex>
      ),
      buttons: ['Cancelar', 'Continuar'],
      onConfirm: async () => {
        await submitBases();
        closeModal();
      },
    });
  }, [openAlert, submitBases, closeModal]);

  const handleConfirm = async (): Promise<void> => {
    const fieldsTab = validateFieldsTab();
    const basesTab = validateBasesTab();

    switch (activeTab) {
      case FIELDS_TAB:
        if (!fieldsTab.isValid) {
          setErrors(fieldsTab.errors);
          return;
        }

        try {
          await submitFields();
          if (isSavingMapping) {
            await handleSaveMappingCapability();
            getCapabilities();
          }

          setActiveTab(1);
          setErrors({} as FormErrors);
        } catch {
          return;
        }

        return;
      case BASES_TAB:
        if (!basesTab.isValid) {
          setConfigErrors(basesTab.errors);

          toast({
            description: 'É necessário configurar pelo menos uma base',
            status: 'error',
          });

          return;
        }

        try {
          if (
            basesTab.basesWithoutMapping &&
            basesTab.basesWithoutMapping.length > 0
          ) {
            warnAboutEmptyBasesConfiguration();
          } else {
            await submitBases();
            closeModal();
          }
        } catch (err) {
          toast({
            description:
              err.response?.data?.message ||
              'Houve um erro inesperado ao importar as bases.',
            status: 'error',
          });
        }

        break;
      default:
        break;
    }
  };

  return (
    <Modal size="3xl" scrollBehavior="inside" {...restProps}>
      <ModalOverlay />
      <ModalContent
        sx={{
          height: `calc(100% - ${modalMargin}px)`,
          maxHeight: `calc(100% - ${modalMargin}px)`,
        }}
      >
        <ModalHeader>Configurar campos</ModalHeader>

        <ModalHeader
          mt="-6"
          fontSize="md"
          fontStyle="italic"
          display="flex"
          gap={2}
          flexWrap="wrap"
        >
          <Text>Orçamento: {budgetDescription}</Text>
          <Text>/</Text>
          <Text
            maxWidth="550px"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            isTruncated
            title={fileName}
          >
            Arquivo: {fileName}
          </Text>
        </ModalHeader>

        {stageDescription && (
          <ModalHeader mt="-7" fontSize="sm" fontStyle="italic">
            Etapa: {stageDescription}
          </ModalHeader>
        )}

        <ModalCloseButton />

        <ModalBody>
          <Tabs
            variant="enclosed"
            colorScheme="red"
            index={activeTab}
            onChange={handleTabsChange}
            marginTop={-2}
          >
            <TabList>
              <Tab _selected={{ fontWeight: 'bold' }}>Campos</Tab>
              <Tab
                _selected={{ fontWeight: 'bold' }}
                isDisabled={Object.keys(configErrors).length <= 0}
              >
                Bases
              </Tab>
            </TabList>

            <TabPanels>
              <TabPanel>
                <FieldsTab
                  headers={headers}
                  actualHeaders={actualHeaders}
                  headerRow={headerRow}
                  errors={errors}
                  fields={fields}
                  setFields={setFields}
                />
              </TabPanel>

              <TabPanel>
                <BasesTab
                  bases={budgetBases}
                  processedBases={mappedColumns.base_description || []}
                  baseConfig={baseConfig}
                  setBaseConfig={setBaseConfig}
                  configErrors={configErrors}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>

          {activeTab === 0 && (
            <Checkbox
              mt={1}
              mr={1}
              isChecked={isSavingMapping}
              onChange={(e) => {
                setIsSavingMapping(e.target.checked);
              }}
              isDisabled={loading}
              colorScheme="blue"
            >
              Salvar configurações de mapeamento{' '}
              <Tooltip
                hasArrow
                label="As configurações de mapeamento serão salvas para que você possa carregar posteriormente e não precise configurá-las novamente na próxima importação."
              >
                <Icon
                  as={InfoCircleFillIcon}
                  className="icon"
                  color="gray.700"
                />
              </Tooltip>
            </Checkbox>
          )}

          {activeTab === 0 && (
            <Flex gap={4}>
              {budgetImportConfigCapability && (
                <Text
                  cursor="pointer"
                  _hover={{
                    textDecoration: 'underline',
                  }}
                  my={4}
                  fontSize="sm"
                  color="blue.500"
                  onClick={() => {
                    handleSetSavedMapping();
                  }}
                >
                  Carregar configurações salvas
                </Text>
              )}

              <Text
                cursor="pointer"
                _hover={{
                  textDecoration: 'underline',
                }}
                my={4}
                fontSize="sm"
                color="blue.500"
                onClick={() => {
                  setIsUpdatingHeader(!isUpdatingHeader);
                }}
              >
                Modificar linha do cabeçalho
              </Text>
            </Flex>
          )}

          {isUpdatingHeader && (
            <>
              <FormControl>
                <FormLabel>Selecione a nova linha do cabeçalho</FormLabel>
                <Input
                  type="number"
                  onChange={(e) => {
                    setNewHeaderLine(Number(e.target.value));
                  }}
                />
              </FormControl>

              <Button
                mt={4}
                colorScheme="blue"
                disabled={newHeaderLine === 0 || newHeaderLine < 0}
                isLoading={loading}
                onClick={async () => {
                  await handleUpdateHeader();
                  setIsUpdatingHeader(false);
                }}
              >
                Atualizar
              </Button>
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <HStack spacing={2}>
            {activeTab === 1 && (
              <Button
                isLoading={loading}
                isDisabled={loading}
                colorScheme="gray"
                onClick={() => {
                  setActiveTab(0);
                }}
              >
                Configurar campos
              </Button>
            )}

            {activeTab === 1 && (
              <Button
                isLoading={loading}
                isDisabled={loading}
                colorScheme="orange"
                onClick={() => {
                  openModal('budgetBaseAdd', {
                    mode: 'add',
                    data: {
                      budgetId,
                      budgetDescription,
                    },
                    onConfirm: () => {
                      getBudget();

                      toast({
                        description: 'Bases adicionadas com sucesso!',
                        status: 'success',
                      });
                    },
                  });
                }}
              >
                Adicionar mais bases
              </Button>
            )}

            <Button
              isLoading={loading}
              isDisabled={loading}
              colorScheme="green"
              onClick={handleConfirm}
            >
              Próximo
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalBudgetImportFields;
